import { ActionType, EntityRegionModeInput, RegionId, WorkflowId } from "@sp-crm/core";
import { workflowPayloadFromWorkflow } from "components/admin/tenant/workflow/workflow-edit";
import { SupportEmailLink } from "components/app/support-email-link";
import { ContentHeader } from "components/layout";
import { EditButton } from "components/ui/action-button";
import { InlineBanner } from "components/ui/inline-banner";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { RegionAccessInput } from "components/ui/region-access-input";
import {
    GetWorkflowSettingsResult,
    GetWorkflowsQuery,
    useUpdateWorkflowMutation,
    WorkflowPayload,
    WorkflowStatus,
} from "generated/graphql";
import React from "react";
import { DelayRecipeEditor } from "./delay-recipe-editor";
import { EditWorkflowPanel } from "./edit-workflow-panel";
import { EditableWorkflow } from "./types";
import { WorkflowActionEditor } from "./workflow-action-editor";
import { WorkflowSettingsField } from "./workflow-settings-field";
import { WorkflowTriggerEditor } from "./workflow-trigger-editor";

interface SingleWorkflowSettingsProps {
    allWorkflows: GetWorkflowsQuery["getWorkflows"];
    workflow: EditableWorkflow;
    scope: "user" | "tenant";
    refetch: () => void;
    workflowSettings: GetWorkflowSettingsResult;
}

export const SingleWorkflowSettings: React.FC<SingleWorkflowSettingsProps> = props => {
    const { workflow, scope, refetch, workflowSettings, allWorkflows } = props;

    const [workflowToEdit, setWorkflowToEdit] = React.useState<EditableWorkflow>(null);

    const updateWorkflowMutation = useUpdateWorkflowMutation();

    const updateWorkflow = React.useCallback(
        async (workflowId: WorkflowId, workflow: WorkflowPayload) => {
            await updateWorkflowMutation.mutateAsync({
                workflowId,
                workflow,
            });

            refetch();
        },
        [updateWorkflowMutation, refetch],
    );

    const handlePublish = React.useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            e.preventDefault();
            const workflowId = workflow?.id;
            if (!workflowId) return;
            if (!allWorkflows || allWorkflows.length === 0) return;
            const existingWorkflow = allWorkflows.find(w => w.id === workflowId);
            if (!existingWorkflow) return;
            const payload = workflowPayloadFromWorkflow(existingWorkflow);
            payload.status = WorkflowStatus.Published;
            await updateWorkflow(workflowId, payload);
        },
        [workflow, allWorkflows, updateWorkflow],
    );

    const updateDelayRecipe = React.useCallback(
        async (workflowId: WorkflowId, delayRecipe: string) => {
            if (!workflowId) return;
            if (!allWorkflows || allWorkflows.length === 0) return;
            const existingWorkflow = allWorkflows.find(w => w.id === workflowId);
            if (!existingWorkflow) return;
            const payload = workflowPayloadFromWorkflow(existingWorkflow);
            payload.delay = 0;
            payload.delayRecipe = delayRecipe;
            await updateWorkflow(workflow.id, payload);
        },
        [workflow, allWorkflows, updateWorkflow],
    );

    const updateRegionVisibility = React.useCallback(
        async (workflowId: WorkflowId, regions: RegionId[]) => {
            if (!workflowId) return;
            if (!allWorkflows || allWorkflows.length === 0) return;
            const existingWorkflow = allWorkflows.find(w => w.id === workflowId);
            if (!existingWorkflow) return;
            const payload = workflowPayloadFromWorkflow(existingWorkflow);
            payload.regionVisibility = {
                regions: regions.map(regionId => ({
                    regionId,
                    mode: EntityRegionModeInput.LocalOnly,
                })),
            };
            await updateWorkflow(workflow.id, payload);
        },
        [workflow, allWorkflows, updateWorkflow],
    );

    const settings = (workflow.settings || []).filter(s => s.scope === scope);
    const editable = !!workflow.regionVisibility?.editable;
    return (
        <div>
            <div className="flex justify-between">
                <ContentHeader>{workflow.name}</ContentHeader>
                {scope === "tenant" && editable ? (
                    <EditButton
                        backgroundColor="bg-white"
                        onClick={() => {
                            setWorkflowToEdit(workflow);
                        }}
                    />
                ) : null}
            </div>
            <div className="space-y-2">
                <p className="text-sm text-gray-500">{workflow.description}</p>
                {scope === "tenant" && !editable ? (
                    <InlineBanner type="info">
                        This workflow requires administrative access to additional Senior
                        Place regions to make changes. Contact your agency administrator
                        or <SupportEmailLink /> to request changes.
                    </InlineBanner>
                ) : null}
                {scope === "tenant" &&
                editable &&
                workflow.status === WorkflowStatus.Draft ? (
                    <InlineBanner type="info">
                        <div className="space-y-4">
                            <div>
                                This workflow is not currently enabled. Please publish the
                                workflow once you have configured the options below.
                            </div>
                            <PrimaryButton onClick={handlePublish}>
                                <span className="text-sm">Publish workflow</span>
                            </PrimaryButton>
                        </div>
                    </InlineBanner>
                ) : null}
                {scope === "tenant" ? (
                    <WorkflowTriggerEditor
                        workflow={workflow}
                        onChange={w => {
                            const payload = workflowPayloadFromWorkflow(w);

                            updateWorkflow(workflow.id, payload);
                        }}
                    />
                ) : null}
                {settings.map(setting => (
                    <WorkflowSettingsField
                        key={`${workflow.id}.${scope}.${setting.key}`}
                        workflow={workflow}
                        setting={setting}
                        workflowSettings={
                            scope === "tenant"
                                ? workflowSettings.tenant
                                : workflowSettings.user
                        }
                        onSaved={refetch}
                    />
                ))}
                {scope === "tenant" ? (
                    <>
                        <WorkflowActionEditor
                            workflow={workflow}
                            onChange={w => {
                                updateWorkflow(
                                    workflow.id,
                                    workflowPayloadFromWorkflow(w),
                                );
                            }}
                        />
                        <DelayRecipeEditor
                            workflow={workflow}
                            onCommit={updateDelayRecipe}
                        />
                        <RegionAccessInput
                            actionType={ActionType.CreateWorkflow}
                            editable={workflow.regionVisibility?.editable}
                            label="Run for clients in these regions"
                            regions={workflow.regionVisibility?.regions?.map(
                                r => r.regionId,
                            )}
                            onChange={regions => {
                                updateRegionVisibility(workflow.id, regions);
                            }}
                        />
                    </>
                ) : null}
            </div>
            <Panel
                type={PanelType.extraLarge}
                headerText="Edit workflow"
                isOpen={!!workflowToEdit}
                onDismiss={() => setWorkflowToEdit(null)}>
                {workflowToEdit ? (
                    <EditWorkflowPanel
                        workflow={workflowToEdit}
                        onSaved={_ => {
                            setWorkflowToEdit(null);
                            refetch();
                        }}
                        onCancel={() => setWorkflowToEdit(null)}
                        onDeleted={_ => {
                            setWorkflowToEdit(null);
                            refetch();
                        }}
                    />
                ) : null}
            </Panel>
        </div>
    );
};
